import React, { useMemo, useState } from 'react';
import {
  Avatar,
  Badge,
  Box,
  Breadcrumbs,
  Button,
  Collapse,
  Divider,
  Drawer,
  IconButton,
  Link as MuiLink,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Toolbar,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  AccountCircle,
  AdminPanelSettings as AdminIcon,
  ArrowDropDown,
  HelpOutline,
  Analytics as AnalyticsIcon,
  Apps as ModulesIcon,
  AttachMoney as CostIcon,
  BarChart as MillingAnalyticsIcon,
  Blender as BlendIcon,
  BugReport as AflatoxinIcon,
  Build as MaintenanceIcon,
  CalendarMonth as ScheduleIcon,
  Dashboard as DashboardIcon,
  DeleteForever as ScrapIcon,
  FactCheck as ComplianceIcon,
  Factory as ShopFloorIcon,
  FormatListBulleted as DispatchListIcon,
  Grain as MillingIcon,
  Hub as ERPIcon,
  Inventory as GrainIcon,
  Inventory2 as InventoryMgmtIcon,
  KeyboardArrowDown,
  KeyboardArrowRight,
  LocalShipping as SupplyChainIcon,
  Logout,
  MedicalServices as FortIcon,
  Menu as MenuIcon,
  MenuBook as RecipeIcon,
  Notifications as NotificationsIcon,
  Person as OperatorIcon,
  PlaylistAddCheck as OrderIcon,
  PrecisionManufacturing as ProductionIcon,
  Restaurant as KitchenOpsIcon,
  Schedule as PlanningIcon,
  Science as QualityTestIcon,
  Settings,
  Storage as MasterDataIcon,
  TrendingUp as DemandForecastIcon,
  Tune as DegermIcon,
  VerifiedUser as QualityIcon,
  Warehouse as SiloIcon,
  WarningAmber as SupplyRiskIcon,
  WaterDrop as TemperIcon,
  AccountTree as GenealogyIcon,
} from '@mui/icons-material';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate, useLocation } from 'react-router-dom';
import { RootState, AppDispatch } from '../../store/store';
import { logoutUser } from '../../store/slices/authSlice';

interface LayoutProps {
  children: React.ReactNode;
}

interface NavigationItem {
  text: string;
  icon: React.ReactNode;
  path: string;
  adminOnly?: boolean;
}

interface NavigationGroup {
  label: string;
  items: NavigationItem[];
}

const drawerWidth = 262;

// Dark sidebar palette (PIPOS-style)
const SB = {
  bg: '#191c21',
  bgHeader: '#14161a',
  text: '#c9cfd6',
  muted: '#8a919b',
  accent: '#f2b32c',
  activeBg: 'rgba(255,255,255,0.08)',
  hoverBg: 'rgba(255,255,255,0.05)',
  divider: 'rgba(255,255,255,0.08)',
  danger: '#ef5350',
};

const Layout: React.FC<LayoutProps> = ({ children }) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [sidebarHidden, setSidebarHidden] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch<AppDispatch>();
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('lg'));
  const authUser = useSelector((state: RootState) => state.auth.user);
  const isAdmin = authUser?.roles?.some((r: string) => r.toLowerCase() === 'admin');
  const displayName = [authUser?.firstName, authUser?.lastName].filter(Boolean).join(' ') || authUser?.email || 'MES User';

  const isActive = (path: string) => (
    path === '/dashboard'
      ? location.pathname === path
      : location.pathname === path || location.pathname.startsWith(`${path}/`)
  );

  // Always-visible quick links (like Dashboard / POS Terminal / Analytics in the sample)
  const topItems: NavigationItem[] = [
    { text: 'Dashboard', icon: <DashboardIcon />, path: '/dashboard' },
    { text: 'Operations Dashboard', icon: <ModulesIcon />, path: '/modules' },
    { text: 'Operator Workstation', icon: <OperatorIcon />, path: '/operator-workstation' },
    { text: 'Analytics', icon: <AnalyticsIcon />, path: '/analytics' },
  ];

  const groups: NavigationGroup[] = [
    {
      label: 'Sales',
      items: [
        { text: 'Customer Orders', icon: <OrderIcon />, path: '/customer-orders' },
        { text: 'Dispatch', icon: <DispatchListIcon />, path: '/scheduling/dispatch' },
      ],
    },
    {
      label: 'Production',
      items: [
        { text: 'Production', icon: <ProductionIcon />, path: '/production' },
        { text: 'Shop Floor', icon: <ShopFloorIcon />, path: '/shopfloor' },
        { text: 'Planning', icon: <PlanningIcon />, path: '/planning' },
        { text: 'Maintenance', icon: <MaintenanceIcon />, path: '/maintenance' },
        { text: 'Scrap Management', icon: <ScrapIcon />, path: '/scrap' },
      ],
    },
    {
      label: 'Quality',
      items: [
        { text: 'Quality Control', icon: <QualityIcon />, path: '/quality' },
        { text: 'HACCP', icon: <QualityTestIcon />, path: '/haccp' },
        { text: 'SPC', icon: <AnalyticsIcon />, path: '/spc' },
        { text: 'Document Control', icon: <MasterDataIcon />, path: '/document-control' },
      ],
    },
    {
      label: 'Milling',
      items: [
        { text: 'Milling Dashboard', icon: <MillingIcon />, path: '/milling' },
        { text: 'Milling Orders', icon: <OrderIcon />, path: '/milling/orders' },
        { text: 'Milling Recipes', icon: <RecipeIcon />, path: '/milling/recipes' },
        { text: 'Cost Configurations', icon: <CostIcon />, path: '/milling/costs' },
        { text: 'Grain Lots', icon: <GrainIcon />, path: '/milling/grain-lots' },
        { text: 'Silo Management', icon: <SiloIcon />, path: '/milling/silos' },
        { text: 'Grist / Blend Recipes', icon: <BlendIcon />, path: '/milling/grist-recipes' },
        { text: 'Tempering Records', icon: <TemperIcon />, path: '/milling/tempering' },
        { text: 'Quality Tests & CoA', icon: <QualityTestIcon />, path: '/milling/quality-tests' },
        { text: 'Aflatoxin Monitoring', icon: <AflatoxinIcon />, path: '/milling/aflatoxin' },
        { text: 'Fortification Records', icon: <FortIcon />, path: '/milling/fortification' },
        { text: 'Maize Degerming', icon: <DegermIcon />, path: '/milling/degerming' },
        { text: 'Milling Schedule', icon: <ScheduleIcon />, path: '/milling/schedule' },
        { text: 'Milling Analytics', icon: <MillingAnalyticsIcon />, path: '/milling/analytics' },
      ],
    },
    {
      label: 'Kitchen Ops',
      items: [
        { text: 'Kitchen Operations', icon: <KitchenOpsIcon />, path: '/kitchen-ops' },
        { text: 'Meal Plans', icon: <RecipeIcon />, path: '/kitchen-ops/meal-planning' },
      ],
    },
    {
      label: 'Supply Chain',
      items: [
        { text: 'Supply Chain', icon: <SupplyChainIcon />, path: '/supply-chain' },
        { text: 'Demand Forecast', icon: <DemandForecastIcon />, path: '/demand-forecasting' },
        { text: 'Supply Risk', icon: <SupplyRiskIcon />, path: '/supply-chain-risk' },
        { text: 'Inventory', icon: <InventoryMgmtIcon />, path: '/inventory' },
        { text: 'Lot Traceability', icon: <GenealogyIcon />, path: '/inventory/lot-genealogy' },
        { text: 'ERP Integration', icon: <ERPIcon />, path: '/erp' },
      ],
    },
    {
      label: 'Setup',
      items: [
        { text: 'Master Data', icon: <MasterDataIcon />, path: '/master-data' },
        { text: 'ISA-95 Compliance', icon: <ComplianceIcon />, path: '/compliance/isa95', adminOnly: true },
        { text: 'Admin', icon: <AdminIcon />, path: '/admin', adminOnly: true },
      ],
    },
  ];

  const visibleItems = (items: NavigationItem[]) => items.filter((item) => !item.adminOnly || isAdmin);

  // Groups start collapsed (like the sample); the group holding the active page opens automatically
  const activeGroupLabel = useMemo(() => {
    const match = groups.find((group) => group.items.some((item) => isActive(item.path)));
    return match?.label || null;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  // Core groups start open for discoverability; user choices persist across sessions
  const DEFAULT_OPEN_GROUPS = ['Sales', 'Production', 'Quality'];
  const GROUP_STATE_KEY = 'mes-sidebar-groups';

  const [openGroups, setOpenGroups] = useState<Record<string, boolean>>(() => {
    try {
      const stored = localStorage.getItem(GROUP_STATE_KEY);
      if (stored) return JSON.parse(stored);
    } catch { /* corrupt state — fall back to defaults */ }
    return {};
  });

  const isGroupOpen = (label: string) =>
    openGroups[label] !== undefined
      ? openGroups[label]
      : DEFAULT_OPEN_GROUPS.includes(label) || label === activeGroupLabel;

  const toggleGroup = (label: string) =>
    setOpenGroups((prev) => {
      const next = { ...prev, [label]: !isGroupOpen(label) };
      try {
        localStorage.setItem(GROUP_STATE_KEY, JSON.stringify(next));
      } catch { /* storage unavailable — state stays in memory */ }
      return next;
    });

  const handleLogout = async () => {
    setAnchorEl(null);
    await dispatch(logoutUser());
    window.location.href = '/login';
  };

  const handleNavigate = (path: string) => {
    navigate(path);
    setMobileOpen(false);
  };

  // Breadcrumbs: Home / <group or page> / <sub page>, labels taken from the nav map
  const pathLabels = useMemo(() => {
    const labels: Record<string, string> = { '/dashboard': 'Dashboard' };
    [...topItems, ...groups.flatMap((g) => g.items)].forEach((item) => {
      labels[item.path] = item.text;
    });
    return labels;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdmin]);

  const prettifySegment = (segment: string) =>
    segment.length > 20
      ? 'Detail'
      : segment.replace(/-/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

  const breadcrumbs = useMemo(() => {
    const segments = location.pathname.split('/').filter(Boolean);
    const crumbs: { label: string; path: string; last: boolean }[] = [];
    let cumulative = '';
    segments.forEach((segment, index) => {
      cumulative += `/${segment}`;
      crumbs.push({
        label: pathLabels[cumulative] || prettifySegment(segment),
        path: cumulative,
        last: index === segments.length - 1,
      });
    });
    return crumbs;
  }, [location.pathname, pathLabels]);

  const renderNavigationItem = (item: NavigationItem, indent = false) => {
    const active = isActive(item.path);
    return (
      <ListItemButton
        key={item.path}
        onClick={() => handleNavigate(item.path)}
        sx={{
          minHeight: 38,
          borderRadius: 1.5,
          mx: 1,
          mb: 0.25,
          px: 1.25,
          pl: indent ? 2 : 1.25,
          color: active ? '#fff' : SB.text,
          bgcolor: active ? SB.activeBg : 'transparent',
          '&:hover': { bgcolor: active ? SB.activeBg : SB.hoverBg },
        }}
      >
        <ListItemIcon sx={{ minWidth: 34, color: active ? SB.accent : SB.muted }}>
          {item.icon}
        </ListItemIcon>
        <ListItemText
          primary={item.text}
          primaryTypographyProps={{
            noWrap: true,
            fontSize: 13.5,
            fontWeight: active ? 700 : 500,
          }}
        />
      </ListItemButton>
    );
  };

  const renderGroup = (group: NavigationGroup) => {
    const items = visibleItems(group.items);
    if (!items.length) return null;
    const open = isGroupOpen(group.label);
    const groupActive = items.some((item) => isActive(item.path));

    return (
      <Box key={group.label}>
        <ListItemButton
          onClick={() => toggleGroup(group.label)}
          disableRipple
          sx={{
            minHeight: 34,
            mx: 1,
            px: 1.25,
            borderRadius: 1.5,
            '&:hover': { bgcolor: SB.hoverBg },
          }}
        >
          <ListItemText
            primary={group.label.toUpperCase()}
            primaryTypographyProps={{
              fontSize: 10.5,
              fontWeight: 800,
              letterSpacing: 1.1,
              color: groupActive ? SB.accent : SB.muted,
              noWrap: true,
            }}
          />
          {open
            ? <KeyboardArrowDown sx={{ fontSize: 16, color: SB.muted }} />
            : <KeyboardArrowRight sx={{ fontSize: 16, color: SB.muted }} />}
        </ListItemButton>
        <Collapse in={open} timeout="auto" unmountOnExit>
          <List disablePadding sx={{ pb: 0.5 }}>
            {items.map((item) => renderNavigationItem(item, true))}
          </List>
        </Collapse>
      </Box>
    );
  };

  const sidebar = (
    <Box
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        bgcolor: SB.bg,
        color: SB.text,
        '&::-webkit-scrollbar': { width: 6 },
      }}
    >
      <Toolbar
        disableGutters
        sx={{
          minHeight: 64,
          px: 2,
          display: 'flex',
          alignItems: 'center',
          gap: 1.25,
          bgcolor: SB.bgHeader,
          borderBottom: `1px solid ${SB.divider}`,
        }}
      >
        <Box
          sx={{
            width: 34,
            height: 34,
            borderRadius: 1,
            bgcolor: SB.accent,
            color: '#1a1a1a',
            display: 'grid',
            placeItems: 'center',
          }}
        >
          <ProductionIcon fontSize="small" />
        </Box>
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="subtitle1" noWrap sx={{ fontWeight: 800, lineHeight: 1.1, color: '#fff', letterSpacing: 0.5 }}>
            MES SYSTEM
          </Typography>
          <Typography variant="caption" noWrap sx={{ color: SB.muted }}>
            Manufacturing execution
          </Typography>
        </Box>
      </Toolbar>

      <Box
        sx={{
          flex: 1,
          overflowY: 'auto',
          py: 1,
          '&::-webkit-scrollbar': { width: 6 },
          '&::-webkit-scrollbar-thumb': { bgcolor: 'rgba(255,255,255,0.15)', borderRadius: 3 },
        }}
      >
        <List disablePadding sx={{ pb: 1 }}>
          {topItems.map((item) => renderNavigationItem(item))}
        </List>
        <Divider sx={{ borderColor: SB.divider, mx: 2, mb: 0.5 }} />
        {groups.map(renderGroup)}
      </Box>

      <Divider sx={{ borderColor: SB.divider }} />
      <List disablePadding sx={{ py: 0.5 }}>
        <ListItemButton
          onClick={(e) => setAnchorEl(e.currentTarget as any)}
          sx={{ minHeight: 40, mx: 1, px: 1.25, borderRadius: 1.5, '&:hover': { bgcolor: SB.hoverBg } }}
        >
          <ListItemIcon sx={{ minWidth: 34 }}>
            <Badge badgeContent={4} color="error">
              <Avatar sx={{ width: 26, height: 26, bgcolor: SB.accent, color: '#1a1a1a', fontSize: 13, fontWeight: 700 }}>
                {displayName.charAt(0).toUpperCase()}
              </Avatar>
            </Badge>
          </ListItemIcon>
          <ListItemText
            primary={displayName}
            secondary={isAdmin ? 'Administrator' : 'Operator'}
            primaryTypographyProps={{ noWrap: true, fontSize: 13.5, fontWeight: 700, color: '#fff' }}
            secondaryTypographyProps={{ noWrap: true, fontSize: 11, color: SB.muted }}
          />
        </ListItemButton>
        <ListItemButton
          onClick={handleLogout}
          sx={{
            minHeight: 38,
            mx: 1,
            mb: 0.5,
            px: 1.25,
            borderRadius: 1.5,
            color: SB.danger,
            '&:hover': { bgcolor: 'rgba(239,83,80,0.12)' },
          }}
        >
          <ListItemIcon sx={{ minWidth: 34, color: SB.danger }}>
            <Logout fontSize="small" />
          </ListItemIcon>
          <ListItemText primary="Logout" primaryTypographyProps={{ fontSize: 13.5, fontWeight: 700 }} />
        </ListItemButton>
      </List>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', bgcolor: 'background.default' }}>
      <Box component="nav" sx={{ width: { lg: sidebarHidden ? 0 : drawerWidth }, flexShrink: { lg: 0 } }}>
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={() => setMobileOpen(false)}
          ModalProps={{ keepMounted: true }}
          sx={{
            display: { xs: 'block', lg: 'none' },
            '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth, bgcolor: SB.bg, border: 0 },
          }}
        >
          {sidebar}
        </Drawer>
        <Drawer
          variant="permanent"
          open
          sx={{
            display: { xs: 'none', lg: sidebarHidden ? 'none' : 'block' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: drawerWidth,
              bgcolor: SB.bg,
              border: 0,
            },
          }}
        >
          {sidebar}
        </Drawer>
      </Box>

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          minWidth: 0,
          display: 'flex',
          flexDirection: 'column',
          minHeight: '100vh',
        }}
      >
        {/* Top navigation bar (sample layout): hamburger + brand | Help + user menu */}
        <Box
          component="header"
          sx={{
            position: 'sticky',
            top: 0,
            zIndex: theme.zIndex.appBar,
            height: 56,
            bgcolor: 'background.paper',
            borderBottom: 1,
            borderColor: 'divider',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            px: { xs: 1.5, md: 2.5 },
            gap: 1,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
            <IconButton
              size="small"
              onClick={() => (isDesktop ? setSidebarHidden((h) => !h) : setMobileOpen(true))}
              aria-label="Toggle navigation"
              sx={{ border: 1, borderColor: 'divider', borderRadius: 1.5 }}
            >
              <MenuIcon fontSize="small" />
            </IconButton>
            {(sidebarHidden || !isDesktop) && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box
                  sx={{
                    width: 26,
                    height: 26,
                    borderRadius: 0.75,
                    bgcolor: SB.accent,
                    color: '#1a1a1a',
                    display: 'grid',
                    placeItems: 'center',
                  }}
                >
                  <ProductionIcon sx={{ fontSize: 16 }} />
                </Box>
                <Typography variant="subtitle2" noWrap sx={{ fontWeight: 800, letterSpacing: 0.5 }}>
                  MES SYSTEM
                </Typography>
              </Box>
            )}
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Button
              size="small"
              variant="outlined"
              color="inherit"
              startIcon={<HelpOutline fontSize="small" />}
              onClick={() => handleNavigate('/modules')}
              sx={{ textTransform: 'none', borderColor: 'divider', color: 'text.secondary', display: { xs: 'none', sm: 'inline-flex' } }}
            >
              Help
            </Button>
            <IconButton size="small">
              <Badge badgeContent={4} color="error">
                <NotificationsIcon fontSize="small" />
              </Badge>
            </IconButton>
            <Button
              size="small"
              color="inherit"
              onClick={(e) => setAnchorEl(e.currentTarget)}
              startIcon={<AccountCircle fontSize="small" />}
              endIcon={<ArrowDropDown fontSize="small" />}
              sx={{ textTransform: 'none', fontWeight: 700, color: 'text.primary', maxWidth: 220 }}
            >
              <Typography noWrap variant="body2" sx={{ fontWeight: 700 }}>
                {displayName}
              </Typography>
            </Button>
          </Box>
        </Box>

        <Box sx={{ p: { xs: 2, md: 3 }, pt: { xs: 2, md: 2 }, flex: 1, minWidth: 0 }}>
        <Breadcrumbs
          separator="/"
          sx={{ mb: 2, '& .MuiBreadcrumbs-separator': { mx: 0.75, color: 'text.disabled' } }}
        >
          <MuiLink
            component="button"
            underline="hover"
            onClick={() => navigate('/dashboard')}
            sx={{ fontSize: 13.5, fontWeight: 600, color: 'success.main', cursor: 'pointer' }}
          >
            Home
          </MuiLink>
          {breadcrumbs.map((crumb) =>
            crumb.last ? (
              <Typography key={crumb.path} sx={{ fontSize: 13.5, color: 'text.secondary' }}>
                {crumb.label}
              </Typography>
            ) : (
              <MuiLink
                key={crumb.path}
                component="button"
                underline="hover"
                onClick={() => navigate(crumb.path)}
                sx={{ fontSize: 13.5, fontWeight: 600, color: 'success.main', cursor: 'pointer' }}
              >
                {crumb.label}
              </MuiLink>
            ),
          )}
        </Breadcrumbs>
        {children}
        </Box>
      </Box>

      <Menu
        anchorEl={anchorEl}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        keepMounted
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        open={Boolean(anchorEl)}
        onClose={() => setAnchorEl(null)}
      >
        <MenuItem onClick={() => { setAnchorEl(null); handleNavigate('/profile'); }}>
          <ListItemIcon><AccountCircle fontSize="small" /></ListItemIcon>Profile
        </MenuItem>
        <MenuItem onClick={() => { setAnchorEl(null); handleNavigate('/profile'); }}>
          <ListItemIcon><Settings fontSize="small" /></ListItemIcon>Settings & Preferences
        </MenuItem>
        <MenuItem onClick={() => setAnchorEl(null)}>
          <ListItemIcon><NotificationsIcon fontSize="small" /></ListItemIcon>Notifications
        </MenuItem>
        <Divider />
        <MenuItem onClick={handleLogout} sx={{ color: SB.danger }}>
          <ListItemIcon><Logout fontSize="small" sx={{ color: SB.danger }} /></ListItemIcon>Logout
        </MenuItem>
      </Menu>
    </Box>
  );
};

export default Layout;
